import React from 'react';
import { formatDistance, calculateDistance } from '../../../services/location.service';

/**
 * LocationIndicator - Shows location status and distance from property
 * @param {Object} props
 * @param {Object} props.currentLocation - {latitude, longitude, accuracy}
 * @param {Object} props.propertyLocation - {latitude, longitude}
 * @param {number} props.thresholdMeters - Distance threshold in meters (default: 75)
 */
function LocationIndicator({ currentLocation, propertyLocation, thresholdMeters = 75 }) {
  if (!currentLocation || !propertyLocation?.latitude || !propertyLocation?.longitude) {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-md text-xs text-gray-500 bg-gray-100">
        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Location unavailable
      </div>
    );
  }

  const distance = calculateDistance(
    currentLocation.latitude,
    currentLocation.longitude,
    propertyLocation.latitude,
    propertyLocation.longitude
  );

  const isAtProperty = distance <= thresholdMeters;
  const isClose = distance <= thresholdMeters * 2; // Within 2x threshold
  const isFar = distance > thresholdMeters * 2;

  const getStatusColor = () => {
    if (isAtProperty) return 'bg-green-100 text-green-800 border-green-200';
    if (isClose) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusIcon = () => {
    if (isAtProperty) {
      return (
        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  };

  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor()}`}>
      {getStatusIcon()}
      <span>
        {isAtProperty ? 'At Property' : `${formatDistance(distance)} from property`}
        {currentLocation.accuracy && (
          <span className="ml-1 text-xs opacity-75">(±{currentLocation.accuracy}m)</span>
        )}
      </span>
    </div>
  );
}

export default LocationIndicator;
